import styles from "./WCMainForm.module.css";
import Wizard from "../../../shared/wizard/Wizard";
import UploadFile from "../../../upload-file/UploadFile";
import FileIcon from "../../../upload-file/file-icon/FileIcon";
import FormSection from "./FormSection/FormSection";
import PreviewSubmission from "../preview-submission/PreviewSubmission";
import NoData from "../../../shared/ui/no-data/NoData";
import StatusBar from "../../../ui/status-bar/StatusBar";
import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import useDocuments from "../../../../hooks/useDocuments";
import * as formActions from "../../../../store/actions/forms";
import * as appActions from "../../../../store/actions/app";
import createUtilities from "../../../../utilities/forms/formInputUtilities";
import { checkField, createValidator, handleFailedField, validateFieldset } from "../../../../utilities/forms/formValidationUtilities";
import { fieldsToStrings } from "../../../../utilities/forms/formSubmissionUtilities";
import {
  zipRegex,
  alphabeticTest,
  phoneTest,
  phoneProps,
  zipTest,
  length100Props,
  length50Props,
  zipProps,
  emailLengthProps,
  emailTest,
  wholeNumbersTest,
  decimalPlacesTest,
  numericTest,
  moneyTest,
  initialTest,
  dateTimeTest,
  hoursPerDayProps,
  length1Props,
  length5Props,
  length500Props,
  length200Props,
  moneyProps,
  trueFalseRequired,
  dateTest,
  freeFormTest,
  univMinDate,
} from "../../../../utilities/validationUtilities";
import {
  addOrSubtractFromDate,
  dateToMoment,
  momentToDate,
} from "../../../../utilities/dateUtilities";
import { formatDate, formatTime } from "../../../../utilities/formatUtilities";
import {
  yesNo,
  yesNoNA,
  gender,
  maritalStatus,
  transportation,
  duty,
  documentDelivery,
  phoneType,
  payPeriod,
  wagePer,
  employmentStatusGW,
} from "../../../../constants/options";
import {
  validationError,
  requiredError,
  noWitnesses,
  noDocuments,
  apiFailureError,
  mandatoryFieldsMessage,
  witnessMessage,
} from "../../../../constants/messages";
import {
  countries as countriesData,
  expandedStates as statesData,
  escalationCriteria,
  US,
} from "../../../../constants/data";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  countryToCode as countryCode,
  stateCodeToName,
} from "../../../../utilities/dataUtilities";
import { dateInRangeIncludesStart } from "../../../../utilities/dateUtilities";
import { colors } from "../../../../constants/colors";

function WCMainForm({ policyNumber }) {

  const dispatch = useDispatch();
  const history = useHistory();

  const injuryPartsData = useSelector((state) => state.forms.wcParts);
  const injuryCausesData = useSelector((state) => state.forms.wcCauses);
  const injuryNaturesData = useSelector((state) => state.forms.wcNatures);

  const policyData = useSelector((state) => state.forms.policyRetrievalData);
  const locations = useSelector(state => state.forms.policyRetrievalData?.locations);
        
  const config = useSelector((state) => state.app.config);
  const apiError = useSelector((state) => state.forms.wcError);
  let insuredUser = useSelector((state) => state.user.userInfo);

  const [countries, setCountries] = useState([]);
  const [injuryParts, setInjuryParts] = useState([]);
  const [injuryCauses, setInjuryCauses] = useState([]);
  const [injuryNatures, setInjuryNatures] = useState([]);
  const [locationDetails, setLocationDetails] = useState([]);

  const [page, setPage] = useState(0);
  const [pageInvalid, setPageInvalid] = useState(false);
  const [shouldValidate, setShouldValidate] = useState(false);
  const [validatorsAdded, setValidatorsAdded] = useState(false);

  const [scrollTarget, setScrollTarget] = useState(null);
  const scrollAnchor = useRef(null);
  const formContainer = useRef(null);

  const [show2Buttons, setShow2Buttons] = useState(false);
  const [enterLocation, setEnterLocation] = useState(true);
  const [noWitnessData, setNoWitnessData] = useState([]);
  const noPhysicalInjury = "Specific Injury - No Physical Injury";

  const locationInfo = "LocationInfo";
  const maxWitnesses = 4;
  const now = new Date();
  now.setSeconds(59);
  const today = new Date(now).setHours(23, 59, 59);

  //additional props for field objects
  const uptoNowProps = { minDate: dateToMoment(univMinDate), maxDate: dateToMoment(now) };
  const uptoTodayProps = { minDate: dateToMoment(univMinDate), maxDate: dateToMoment(today) };

  //object that defines the form fields along with their labels, default values, and properties, etc.
  //several 100 lines long, collapsing the object is suggested
  const fields = {
    reporterFirst: {
      label: "First Name",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
      page: 0,
    },
    reporterLast: {
      label: "Last Name",
      additionalProps: length50Props,
      required: true,
      regex: alphabeticTest,
      value: "",
      page: 0,
    },
    reporterTitle: {
      label: "Title",
      additionalProps: length100Props,
      regex: freeFormTest,
      value: "",
      page: 0,
    },
    reporterPhone: {
      label: "Phone",
      additionalProps: phoneProps,
      regex: phoneTest,
      value: "",
      page: 0,
    },
    reporterExt: {
      label: "Ext.",
      additionalProps: length5Props,
      regex: numericTest,
      value: "",
      page: 0,
    },
    reporterPhoneType: {
      label: "What type of phone is this?",
      value: "",
      page: 0,
    },
    reporterEmail: {
      label: "Email",
      additionalProps: emailLengthProps,
      regex: emailTest,
      value: "",
      page: 0,
    },
    reporterDocumentDelivery: {
      label:
        "What is your preferred method of receiving claim-related documents?",
      value: "",
      page: 0,
    },
    reporterTextOptIn: {
      label: "Would you like to opt into our texting service to receive updates on the claim from your adjuster and to respond to updates via SMS/text messaging?",
      value: null,
      page: 0,
    },
    reporterIsMainContact: {
      fieldName: "reporterIsMainContact",
      label: "Are you the contact for communications related to this claim?",
      errorMessage: requiredError,
      required: true,
      validation: trueFalseRequired,
      value: true,
      page: 0,
    },
    reportContactFirst: {
      label: "First Name",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
      page: 0,
    },
    reportContactLast: {
      label: "Last Name",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
      page: 0,
    },
    reportContactPhone: {
      label: "Phone",
      additionalProps: phoneProps,
      regex: phoneTest,
      value: "",
      page: 0,
    },
    reportContactExt: {
      label: "Ext.",
      additionalProps: length5Props,
      regex: numericTest,
      value: "",
      page: 0,
    },
    reportContactPhoneType: {
      label: "What type of phone is this?",
      value: "",
      page: 0,
    },
    reportContactEmail: {
      label: "Email",
      additionalProps: emailLengthProps,
      regex: emailTest,
      value: "",
      page: 0,
    },
    reportContactDocumentDelivery: {
      label:
        "What is the preferred method of delivering claim-related documents to the contact for this claim?",
      value: "",
      page: 0,
    },
    incidentDate: {
      label: "Incident Date",
      additionalProps: uptoNowProps,
      regex: dateTimeTest,
      required: true,
      value: null,
      page: 0,
    },
    employerNotifiedDate: {
      label: "Employer Notified",
      additionalProps: uptoNowProps,
      regex: dateTimeTest,
      value: null,
      page: 0,
    },
    incidentDescription: {
      label: "Incident Description",
      additionalProps: length500Props,
      regex: freeFormTest,
      required: true,
      value: "",
      page: 0,
    },
    theLocationInfo: {
      label: "Location",
      required: true,
      value: "",
      displayValue: null,
      page: 1,
    },
    clientLocationName: {
      label: "Location Name",
      additionalProps: length200Props,
      regex: freeFormTest,
      value: "",
      page: 1,
    },
    clientLocationNumber: {
      label: "Location Number",
      additionalProps: { maxLength: 3 },
      regex: numericTest,
      value: "",
      page: 1,
    },
    clientLocationAddr: {
      label: "Address",
      additionalProps: length100Props,
      regex: freeFormTest,
      value: "",
      page: 1,
    },
    clientLocationAddr2: {
      label: "Address Line 2",
      additionalProps: length50Props,
      regex: freeFormTest,
      value: "",
      page: 1,
    },
    clientLocationCity: {
      label: "City",
      additionalProps: length50Props,
      regex: alphabeticTest,
      required: true,
      value: "",
      page: 1,
    },
    clientLocationState: {
      label: "State",
      required: true,
      value: "",
      displayValue: null,
      page: 1,
    },
    clientLocationZip: {
      label: "ZIP Code",
      additionalProps: zipProps,
      regex: zipTest,
      value: "",
      page: 1,
    },
    clientLocationCounty: {
      label: "County",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
      page: 1,
    },
    clientLocationCountry: {
      label: "Country",
      required: true,
      value: US.name, //hardcoded to country to US, is always US
      page: 1,
    },
    clientLocationPhone: {
      label: "Phone",
      additionalProps: phoneProps,
      regex: phoneTest,
      value: "",
      page: 1,
    },
    clientLocationExt: {
      label: "Ext.",
      additionalProps: length5Props,
      regex: numericTest,
      value: "",
      page: 1,
    },
    atClientLocation: {
      fieldName: "atClientLocation",
      label: "Did the incident occur at the business location?",
      errorMessage: requiredError,
      required: true,
      validation: trueFalseRequired,
      value: null,
      page: 1,
    },
    incidentLocationName: {
      label: "Location Name",
      additionalProps: length200Props,
      regex: freeFormTest,
      value: "",
      page: 1,
    },
    incidentLocationAddr: {
      label: "Address",
      additionalProps: length100Props,
      regex: freeFormTest,
      value: "",
      page: 1,
    },
    incidentLocationAddr2: {
      label: "Address Line 2",
      additionalProps: length50Props,
      regex: freeFormTest,
      value: "",
      page: 1,
    },
    incidentLocationCity: {
      label: "City",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
      page: 1,
    },
    incidentLocationState: {
      label: "State",
      value: "",
      displayValue: null,
      required: true,
      page: 1,
    },
    incidentLocationZip: {
      label: "ZIP Code",
      additionalProps: zipProps,
      regex: zipTest,
      value: "",
      page: 1,
    },
    incidentLocationCounty: {
      label: "County",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
      page: 1,
    },
    incidentLocationCountry: {
      label: "Country",
      value: US.name,
      page: 1,
    },
    claimantFirst: {
      label: "First Name",
      additionalProps: length50Props,
      regex: alphabeticTest,
      required: true,
      value: "",
      page: 2,
    },
    claimantMidInitial: {
      label: "Middle Initial",
      additionalProps: length1Props,
      regex: initialTest,
      value: "",
      page: 2,
    },
    claimantLast: {
      label: "Last Name",
      additionalProps: length50Props,
      regex: alphabeticTest,
      required: true,
      value: "",
      page: 2,
    },
    claimantAddr: {
      label: "Address",
      additionalProps: length100Props,
      regex: freeFormTest,
      value: "",
      page: 2,
    },
    claimantAddr2: {
      label: "Address Line 2",
      additionalProps: length50Props,
      regex: freeFormTest,
      value: "",
      page: 2,
    },
    claimantCity: {
      label: "City",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
      page: 2,
    },
    claimantState: {
      label: "State",
      value: "",
      displayValue: null,
      page: 2,
    },
    claimantZip: {
      label: "ZIP Code",
      additionalProps: zipProps,
      regex: zipTest,
      value: "",
      page: 2,
    },
    claimantCounty: {
      label: "County",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
      page: 2,
    },
    claimantCountry: {
      label: "Country",
      value: US.name,
      page: 2,
    },
    claimantBestPhone: {
      label: "Best Phone",
      additionalProps: phoneProps,
      regex: phoneTest,
      value: "",
      page: 2,
    },
    claimantPhoneType: {
      label: "What type of phone is this?",
      value: "",
      page: 2,
    },
    claimantEmail: {
      label: "Email",
      additionalProps: emailLengthProps,
      regex: emailTest,
      value: "",
      page: 2,
    },
    claimantSSN: {
      label: "SSN",
      additionalProps: { maxLength: 9, minLength: 9 },
      regex: numericTest,
      masked: true,
      value: "",
      page: 2,
    },
    claimantGender: {
      label: "Gender",
      value: "",
      page: 2,
    },
    claimantMaritalStatus: {
      label: "Marital Status",
      value: "",
      page: 2,
    },
    claimantNumberDependents: {
      label: "Number of Dependents",
      additionalProps: { min: 0, max: 250 },
      regex: wholeNumbersTest,
      value: "",
      page: 2,
    },
    claimantDateOfBirth: {
      label: "Date of Birth",
      additionalProps: { minDate: dateToMoment(univMinDate), maxDate: dateToMoment(addOrSubtractFromDate(today, -16, "years")) },
      regex: dateTest,
      value: null,
      page: 2,
    },
    fatalityInvolved: {
      label: "Was there a fatality?",
      value: false,
      page: 2,
    },
    dateOfDeath: {
      label: "Date of Death",
      additionalProps: uptoTodayProps,
      regex: dateTest,
      value: null,
      page: 2,
    },
    supervisorFirst: {
      label: "First Name",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
      page: 2,
    },
    supervisorMidInitial: {
      label: "Middle Initial",
      additionalProps: length1Props,
      regex: initialTest,
      value: "",
      page: 2,
    },
    supervisorLast: {
      label: "Last Name",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
      page: 2,
    },
    supervisorTitle: {
      label: "Title",
      additionalProps: length100Props,
      regex: freeFormTest,
      value: "",
      page: 2,
    },
    supervisorPhone: {
      label: "Phone",
      additionalProps: phoneProps,
      regex: phoneTest,
      value: "",
      page: 2,
    },
    supervisorExt: {
      label: "Ext.",
      additionalProps: length5Props,
      regex: numericTest,
      value: "",
      page: 2,
    },
    supervisorEmail: {
      label: "Email",
      additionalProps: emailLengthProps,
      regex: emailTest,
      value: "",
      page: 2,
    },
    lostTime: {
      label: "Lost Time",
      value: "",
      page: 2,
    },
    salaryContinued: {
      label: "Salary Continued",
      value: "",
      page: 2,
    },
    receivedFullWage: {
      label: "Received Full Wage",
      value: "",
      page: 2,
    },
    lastDateWorked: {
      label: "Last Date Worked",
      additionalProps: uptoTodayProps,
      regex: dateTest,
      value: null,
      page: 2,
    },
    returnedToWork: {
      label: "Has the employee returned to work?",
      value: null,
      page: 2,
    },
    returnedDate: {
      label: "What date did they return?",
      additionalProps: uptoTodayProps,
      regex: dateTest,
      required: true,
      value: null,
      page: 2,
    },
    returnedDuty: {
      label: "Did the Employee return to work on light or full duty?",
      required: true,
      value: "",
      page: 2,
    },
    jobTitle: {
      label: "Job Title",
      additionalProps: length100Props,
      regex: freeFormTest,
      value: "",
      page: 2,
    },
    jobStatus: {
      label: "Status",
      value: "",
      displayValue: null,
      page: 2,
    },
    jobHireDate: {
      label: "Hire Date",
      additionalProps: uptoTodayProps,
      regex: dateTest,
      value: null,
      page: 2,
    },
    jobTerminationDate: {
      label: "Termination Date",
      additionalProps: uptoTodayProps,
      regex: dateTest,
      value: null,
      page: 2,
    },
    jobHireState: {
      label: "Hire State",
      value: "",
      displayValue: null,
      page: 2,
    },
    jobWage: {
      label: "Wage",
      additionalProps: moneyProps,
      regex: moneyTest,
      value: "",
      page: 2,
    },
    jobWageFrequency: {
      label: "per",
      value: wagePer[0],
      page: 2,
    },
    jobPayPeriod: {
      label: "How often is the employee paid?",
      value: "",
      page: 2,
    },
    jobAverageHoursPerDay: {
      label: "What is the average number of hours the employee works per day?",
      value: "",
      additionalProps: hoursPerDayProps,
      regex: decimalPlacesTest,
      page: 2,
    },
    jobSun: {
      label: "Sunday",
      previewLabel: "Does the employee work on Sunday?",
      value: false,
      page: 2,
    },
    jobMon: {
      label: "Monday",
      previewLabel: "Does the employee work on Monday?",
      value: false,
      page: 2,
    },
    jobTues: {
      label: "Tuesday",
      previewLabel: "Does the employee work on Tuesday?",
      value: false,
      page: 2,
    },
    jobWed: {
      label: "Wednesday",
      previewLabel: "Does the employee work on Wednesday?",
      value: false,
      page: 2,
    },
    jobThurs: {
      label: "Thursday",
      previewLabel: "Does the employee work on Thursday?",
      value: false,
      page: 2,
    },
    jobFri: {
      label: "Friday",
      previewLabel: "Does the employee work on Friday?",
      value: false,
      page: 2,
    },
    jobSat: {
      label: "Saturday",
      previewLabel: "Does the employee work on Saturday?",
      value: false,
      page: 2,
    },
    injuryIs: {
      fieldName: "InjuryIs",
      label: "Was the party injured?",
      required: true,
      validation: trueFalseRequired,
      errorMessage: requiredError,
      value: null,
      page: 2,
    },
    injuryTreatmentSought: {
      label: "Was medical treatment sought?",
      value: null,
      page: 2,
    },
    injuryPart: {
      label: "Body Part",
      required: true,
      value: "",
      displayValue: null,
      page: 2,
    },
    injuryCause: {
      label: "Cause",
      required: true,
      value: "",
      displayValue: null,
      page: 2,
    },
    injuryNature: {
      label: "Nature",
      required: true,
      value: "",
      displayValue: null,
      page: 2,
    },
    hospitalName: {
      label: "Hospital/Clinic",
      additionalProps: length200Props,
      regex: freeFormTest,
      required: true,
      value: "",
      page: 2,
    },
    hospitalAddr: {
      label: "Address",
      additionalProps: length100Props,
      regex: freeFormTest,
      value: "",
      page: 2,
    },
    hospitalAddr2: {
      label: "Address Line 2",
      additionalProps: length50Props,
      regex: freeFormTest,
      value: "",
      page: 2,
    },
    hospitalCity: {
      label: "City",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
      page: 2,
    },
    hospitalState: {
      label: "State",
      value: "",
      displayValue: null,
      page: 2,
    },
    hospitalZip: {
      label: "ZIP Code",
      additionalProps: zipProps,
      regex: zipTest,
      value: "",
      page: 2,
    },
    hospitalCounty: {
      label: "County",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
      page: 2,
    },
    hospitalCountry: {
      label: "Country",
      value: US.name,
      page: 2,
    },
    hospitalPhone: {
      label: "Phone",
      additionalProps: phoneProps,
      regex: phoneTest,
      value: "",
      page: 2,
    },
    hospitalExt: {
      label: "Ext.",
      additionalProps: length5Props,
      regex: numericTest,
      value: "",
      page: 2,
    },
    hospitalTransportation: {
      label: "Transportation Type",
      value: "",
      page: 2,
    },
    doctorFirst: {
      label: "Doctor's First Name",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
      page: 2,
    },
    doctorLast: {
      label: "Doctor's Last Name",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
      page: 2,
    },
    hospitalAddrDiffers: {
      label: "Doctor's address differs from hospital?",
      value: false,
      page: 2,
    },
    doctorAddr: {
      label: "Address",
      additionalProps: length100Props,
      regex: freeFormTest,
      value: "",
      page: 2,
    },
    doctorAddr2: {
      label: "Address Line 2",
      additionalProps: length50Props,
      regex: freeFormTest,
      value: "",
      page: 2,
    },
    doctorCity: {
      label: "City",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
      page: 2,
    },
    doctorState: {
      label: "State",
      value: "",
      displayValue: null,
      page: 2,
    },
    doctorZip: {
      label: "ZIP Code",
      additionalProps: zipProps,
      regex: zipTest,
      value: "",
      page: 2,
    },
    doctorCounty: {
      label: "County",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
      page: 2,
    },
    doctorCountry: {
      label: "Country",
      value: US.name,
      page: 2,
    },
    comments: {
      label: "Notes/Additional Comments",
      additionalProps: length500Props,
      regex: freeFormTest,
      value: "",
      page: 4,
    },
    escalationCriteria: {
      label: "Does this claim meet any escalation criteria below?",
      value: null,
      page: 4,
    },
  };

  //defines format of each witness object
  const witness = {
    firstName: {
      label: "First Name",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
    },
    lastName: {
      label: "Last Name",
      additionalProps: length50Props,
      regex: alphabeticTest,
      value: "",
    },
    bestPhone: {
      label: "Best contact phone",
      additionalProps: phoneProps,
      regex: phoneTest,
      value: "",
    },
    ext: {
      label: "Ext.",
      additionalProps: length5Props,
      regex: numericTest,
      value: "",
    },
  };

  //stores format of witness object after validations added
  let [witnessObj, setWitnessObj] = useState(null);

  //these 3 variables hold all of the form submission data
  let [witnessInfo, setWitnessInfo] = useState([]);
  let [documents, setDocuments] = useState({ files: [], size: 0 });
  let [formFields, setFormFields] = useState(fields);

  let [previewData, setPreviewData] = useState([]);

  const handleDocuments = useDocuments(documents, setDocuments);

  const {
    createInput,
    createSecretInput,
    createTextArea,
    createDateInput,
    createToggleInput,
    createSelect,
    createCheckbox,
    createSearchableSelect,
    handleFieldChange,
  } = createUtilities(formFields, setFormFields, styles);

  //sections object defines section headings and ids for display and preview
  const sections = {
    YourInfo: {
      heading: "Your Information",
      id: "userinfo",
      page: 0,
    },
    ReportContact: {
      heading: "Contact For This Claim",
      id: "reportContact",
      page: 0,
    },
    IncidentDetails: {
      heading: "Incident Details",
      id: "incident",
      page: 0,
    },
    LocationInfo: {
      heading: "Location Information",
      id: "locationInfo",
      page: 1,
    },
    IncLocation: {
      heading: "Incident Location",
      id: "incidentLocation",
      page: 1,
    },
    Claimant: {
      heading: "Claimant",
      id: "claimant",
      page: 2,
    },
    Supervisor: {
      heading: "Supervisor",
      id: "supervisor",
      page: 2,
    },
    LostTime: {
      heading: "Lost Time",
      id: "lostTime",
      page: 2,
    },
    Employment: {
      heading: "Employment",
      id: "employment",
      page: 2,
    },
    InjuryInfo: {
      heading: "Injury Information",
      id: "injuryInfo",
      page: 2,
    },
    MedicalTreatment: {
      heading: "Medical Treatment",
      id: "medical",
      page: 2,
    },
    WitnessInfo: {
      heading: "Witness Information",
      id: "witnesses",
      page: 3,
    },
    Notes: {
      heading: "Notes",
      id: "notes",
      page: 4,
    },
    Escalation: {
      heading: "Additional Escalation Criteria",
      id: "escalation",
      page: 4,
    },
    Documents: {
      heading: "Documents",
      id: "documents",
      page: 4,
    },
  };

  const filterFields = (prefix, specificList) => {
    let theFields = [];
    if (prefix) {
      theFields = Object.keys(formFields)
        .filter((key) => key.startsWith(prefix))
        .map((key) => {
          return { ...formFields[key] };
        });
    }
    if (specificList) {
      specificList.forEach((field) => theFields.push({ ...field }));
    }
    theFields = theFields
      .filter((field) => {
        return field.fieldName !== formFields.jobWageFrequency.fieldName;//because included with jobWage - see below
      })
      .map((field) => {
        if (field.fieldName === formFields.jobWage.fieldName) {
           field.value =
            field.value + " per " + formFields.jobWageFrequency.value;
        }
        if (field.previewLabel?.length > 0) {
          field.label = field.previewLabel;
        }
        return field;
      });
    return theFields;
  }; //filter to a subset of formFields, by a prefix or specific list or both - helper for previewSubmissionData

  const navigateToElem = (page, id) => {
    setPage(page);
    setScrollTarget(id);
  }; //for edit function for previewSubmission

  const sectionData = (section, theFields) => {
    return {
      heading: section.heading,
      fields: theFields,
      edit: () => navigateToElem(section.page, section.id),
    };
  }; //creates preview data for a section

  const formatDateTime = (field) => {
    return {
      fieldName: field.fieldName,
      label: field.label,
      value: field.value
        ? `${formatDate(field.value)} ${formatTime(field.value)}`
        : null,
    };
  }; // to format datetime fields for preview display

  const createPreviewData = () => {
    setPreviewData([
      sectionData(
        sections.YourInfo,
        insuredUser
          ? filterFields("reporter")
          : filterFields("reporter").filter(
              (field) =>
                field.fieldName !==
                formFields.reporterDocumentDelivery.fieldName &&
                field.fieldName !==
                formFields.reporterTextOptIn.fieldName
            )
      ),
      !formFields.reporterIsMainContact.value
        ? sectionData(
            sections.ReportContact,
            insuredUser
              ? filterFields("reportContact")
              : filterFields("reportContact").filter(
                  (field) =>
                    field.fieldName !==
                    formFields.reportContactDocumentDelivery.fieldName
                )
          )
        : null,
      sectionData(
        sections.IncidentDetails,
        [
          formatDateTime(formFields.incidentDate),
          formatDateTime(formFields.employerNotifiedDate),
        ].concat(filterFields("", [formFields.incidentDescription]))
      ),
      sectionData(
        sections.LocationInfo,
        enterLocation
          ? filterFields("clientLocation", [formFields.atClientLocation])
          : filterFields("", [
              formFields.theLocationInfo,
              formFields.atClientLocation,
            ])
      ),
      !formFields.atClientLocation.value
        ? sectionData(sections.IncLocation, filterFields("incidentLocation"))
        : null,
      sectionData(
        sections.Claimant,
        filterFields(
          "claim",
          formFields.fatalityInvolved.value
            ? [formFields.fatalityInvolved, formFields.dateOfDeath]
            : [formFields.fatalityInvolved]
        )
      ),
      sectionData(sections.Supervisor, filterFields("sup")),
      sectionData(
        sections.LostTime,
        formFields.returnedToWork.value
          ? filterFields("", [
              formFields.lostTime,
              formFields.salaryContinued,
              formFields.receivedFullWage,
              formFields.lastDateWorked,
            ]).concat(filterFields("returned"))
          : filterFields("", [
              formFields.lostTime,
              formFields.salaryContinued,
              formFields.receivedFullWage,
              formFields.lastDateWorked,
              formFields.returnedToWork,
            ])
      ),
      sectionData(sections.Employment, filterFields("job")),
      sectionData(
        sections.InjuryInfo,
        formFields.injuryIs.value
          ? filterFields("injury")
          : filterFields("", [formFields.injuryIs, formFields.injuryCause])
      ),
      formFields.injuryTreatmentSought.value
        ? sectionData(
            sections.MedicalTreatment,
            formFields.hospitalAddrDiffers.value
              ? filterFields("hospital").concat(filterFields("doctor"))
              : filterFields("hospital", [
                  formFields.doctorFirst,
                  formFields.doctorLast,
                ])
          )
        : null,
      sectionData(
        sections.WitnessInfo,
        witnessInfo.length > 0
          ? witnessInfo.map((witness) => {
              const witnessArray = [];
              for (let fieldName in witness) {
                witnessArray.push(witness[fieldName]);
              }
              return witnessArray;
            }) //map witness objs into arrays for preview submission
          : [
              {
                fieldName: "witness",
                label: "",
                value: noWitnesses,
              },
            ]
      ),
      sectionData(sections.Notes, filterFields("comments")),
      sectionData(sections.Escalation, filterFields("escalation")),
      sectionData(
        sections.Documents,
        documents.files.length > 0
          ? documents.files.map((file, index) => {
              return { fieldName: index, label: "", value: file.name };
            })
          : [
              {
                fieldName: "documents",
                label: "",
                value: noDocuments,
              },
            ]
      ),
    ]);
  }; //sets preview data

  const validatePage = () => {
    if (page === 3) {
      let allWitnessFields = [];
      let noData = [];
      const newWitnessInfo = [...witnessInfo];
      newWitnessInfo.forEach((witness) => {
        let noWitData = true;
        Object.values(witness).forEach((field) => {
          allWitnessFields.push({ valid: checkField(field) });
          if (field.value?.trim()) {
            noWitData = false;
          }
        });
        if (noWitData) {
          Object.values(witness).forEach((field) => (field.visited = true));
        }
        noData.push(noWitData);
      });
      setNoWitnessData(noData);
      setWitnessInfo(newWitnessInfo);
      if (allWitnessFields.find((field) => field.valid !== true)) {
        return false;
      }
      if (noData.find((item) => item)) {
        return false;
      } //there is a witness with all blank fields
      return true;
    } else {
      let pageFields = Object.values(formFields)
        .map((obj, index) => {
          return { ...obj, index: index };
        })
        .filter((field) => field.page === page);
      if (page === 1 && enterLocation) {
        pageFields = pageFields.filter(
          (field) => field.fieldName !== formFields.theLocationInfo.fieldName
        );
      }
      if (page === 1 && !enterLocation) {
        pageFields = pageFields.filter(
          (field) =>
            field.fieldName !== formFields.clientLocationCountry.fieldName &&
            field.fieldName !== formFields.clientLocationState.fieldName &&
            field.fieldName !== formFields.clientLocationCity.fieldName
        );
      }
      if (page === 1 && formFields.atClientLocation.value) {
        pageFields = pageFields.filter(
          field => field.fieldName !== formFields.incidentLocationState.fieldName
        );
      }
      if (page === 2 && !formFields.injuryIs.value) {
        pageFields = pageFields.filter(
          (field) => field.fieldName !== formFields.injuryPart.fieldName
        );
      }
      if (page === 2 && !formFields.injuryTreatmentSought.value) {
        pageFields = pageFields.filter(
          (field) => field.fieldName !== formFields.hospitalName.fieldName
        );
      }
      if (page === 2 && !formFields.returnedToWork.value) {
        pageFields = pageFields.filter(
          (field) =>
            field.fieldName !== formFields.returnedDate.fieldName &&
            field.fieldName !== formFields.returnedDuty.fieldName
        );
      }
      return validateFieldset(pageFields, formFields, setFormFields, true);
    }
  }; //validates fields on the current page

  const addValidatorsNames = (theFields, setFields) => {
    let newFields = { ...theFields };
    for (let key in newFields) {
      if (
        newFields[key].fieldName !== formFields.atClientLocation.fieldName &&
        newFields[key].fieldName !== formFields.injuryIs.fieldName &&
        newFields[key].fieldName !== formFields.reporterIsMainContact.fieldName
      ) {
        const theField = newFields[key];
        newFields[key] = {
          ...theField,
          fieldName: key,
          validation: createValidator({
            ...theField.additionalProps,
            ...theField.regex,
            label: theField.label,
            required: theField.required,
          }),
        };
      }
    }
    setFields(newFields);
  }; //adds validation functions to fields objects, as well as fieldNames

  const concatenateOption = (item) => item?.Category + " - " + item?.Text; //helper for transforming injury items for display

    const submitForm = () => {
        const formData = { ...formFields };
        fieldsToStrings(formData);
        if (!enterLocation) {
          const locationNumber = formData.theLocationInfo.split("-")[0].trim();

          const chosenLocation = locations.find(
            (location) => location.locationNumber === locationNumber
          );
          formData.clientLocationNumber =
            locationNumber?.length > 3 ? locationNumber.slice(-3) : locationNumber;
          formData.clientLocationNumber = chosenLocation.locationNumber;
          formData.clientLocationAddr = chosenLocation.addressLine1;
          formData.clientLocationCity = chosenLocation.city;
          formData.clientLocationState = chosenLocation.state;
          if (zipRegex.test(chosenLocation.postalCode)) {
            formData.clientLocationZip = chosenLocation.postalCode;
          }
          formData.clientLocationId = chosenLocation.policySystemId_Ext;
        }
        const mainLocation = locations?.find(
          (location) => location.LocationNumber === "1"
        );
        if (mainLocation) {
          formData.mainLocationAddr = mainLocation.addressLine1;
          formData.mainLocationCity = mainLocation.city;
          formData.mainLocationState = mainLocation.state;
          formData.mainLocationZip = mainLocation.postalCode;
          formData.mainLocationCountry = US.code;
        }
        const getAbbr = (field) =>
        statesData.find((item) => item.state === field)?.Abbr || field;
        formData.clientLocationState = getAbbr(formData.clientLocationState);
        formData.mainLocationState = getAbbr(formData.mainLocationState);
        formData.incidentLocationState = getAbbr(formData.incidentLocationState);
        formData.claimantState = getAbbr(formData.claimantState);
        formData.hospitalState = getAbbr(formData.hospitalState);
        formData.doctorState = getAbbr(formData.doctorState);
        formData.jobHireState = getAbbr(formData.jobHireState);
        formData.clientLocationCountry = countryCode(
            formData.clientLocationCountry
        );
        formData.mainLocationCountry = countryCode(formData.mainLocationCountry);
        formData.incidentLocationCountry = countryCode(
            formData.incidentLocationCountry
        );
        formData.claimantCountry = countryCode(formData.claimantCountry);
        formData.hospitalCountry = countryCode(formData.hospitalCountry);
        formData.doctorCountry = countryCode(formData.doctorCountry);
        formData.isInjured = formData.injuryIs;
        delete formData.injuryIs;
        formData.soughtTreatment = formData.injuryTreatmentSought;
        delete formData.injuryTreatmentSought;
        formData.jobAverageHoursPerDay = formData.jobAverageHoursPerDay === "0" ? null : formData.jobAverageHoursPerDay;
        const findInjuryItem = (array, value) =>
            array.find((item) => value === concatenateOption(item));
        const injuryPart = findInjuryItem(injuryPartsData, formData.injuryPart);
        const injuryCause = findInjuryItem(injuryCausesData, formData.injuryCause);
        const injuryNature = findInjuryItem(
            injuryNaturesData,
            formData.injuryNature
        );
        formData.injuryPart = injuryPart?.Text;
        formData.injuryCause = injuryCause.Text;
        formData.injuryNature = injuryNature.Text;
        formData.injuryPartCode = injuryPart?.Code;
        formData.injuryCauseCode = injuryCause.Code;
        formData.injuryNatureCode = injuryNature.Code;
        formData.injuryArea = injuryPart?.CategoryCode;
        formData.injuryGeneralCause = injuryCause.CategoryCode;
        formData.injuryGeneralNature = injuryNature.CategoryCode;
        formData.injuryAreaFull = injuryPart?.Category;
        formData.injuryGeneralCauseFull = injuryCause.Category;
        formData.injuryGeneralNatureFull = injuryNature.Category;
        const witnesses = [...witnessInfo].map((witness) =>
            fieldsToStrings({ ...witness })
        );
        formData.witnesses = witnesses;
        formData.policySuccess = policyData?.policyNumber !== undefined;
    
    dispatch(
      formActions.sendWCForm(formData, documents.files, history, policyNumber)
    );
  }; //handles form submission

  const createWitness = (witness, index) => {
    const setWitness = (input) => {
      const oldWitnessInfo = [...witnessInfo];
      oldWitnessInfo[index] = input;
      setWitnessInfo(oldWitnessInfo);
    };
    const createWitInput = createUtilities(
      witness,
      setWitness,
      styles
    ).createInput;
    return (
      <li key={index}>
        <div className={styles.row}>
          {createWitInput(witness.firstName, "45%")}
          {createWitInput(witness.lastName, "45%")}
        </div>

        <div className={styles.row}>
          {createWitInput(witness.bestPhone, "45%", "tel")}
          {createWitInput(witness.ext, "10%")}
          <div className={styles.removeWitWrapper}>
            <button
              onClick={(event) => {
                event.preventDefault();
                const info = [...witnessInfo];
                info.splice(index, 1);
                setWitnessInfo(info);
                const nodataInfo = [...noWitnessData];
                nodataInfo.splice(index, 1);
                setNoWitnessData(nodataInfo);
              }}
              className={styles.emphasizedLink}
            >
              Remove Witness {index + 1}
            </button>
            {noWitnessData && noWitnessData[index] && (
              <p className={styles.witnessError}>
                {witnessMessage.replace("$", index + 1)}
              </p>
            )}
          </div>
        </div>
      </li>
    );
  }; //displays fields for a single witness

  const pages = [
    <>
      <FormSection section={sections.YourInfo}>
        <div className={styles.row}>
          {createInput(formFields.reporterFirst, "45%")}
          {createInput(formFields.reporterLast, "45%")}
        </div>
        <div className={styles.row}>
          {createInput(formFields.reporterTitle, "45%")}
          {createInput(formFields.reporterEmail, "45%", "email")}
        </div>
        <div className={styles.row}>
          {createInput(formFields.reporterPhone, "45%", "tel")}
          {createInput(formFields.reporterExt, "15%")}
          {createSelect(formFields.reporterPhoneType, "30%", phoneType)}
        </div>
        {insuredUser && (
          <>
          <div className={styles.row}>
            {createSelect(
              formFields.reporterDocumentDelivery,
              "94%",
              documentDelivery
            )}
          </div>
          <div className={styles.row}>
            {createToggleInput(
              formFields.reporterTextOptIn,
              "100%",
              yesNo
            )}
          </div>
          </>
        )}

        <div className={styles.row}>
          {createToggleInput(formFields.reporterIsMainContact, "100%", yesNo)}
        </div>
      </FormSection>
      {formFields.reporterIsMainContact.value === false && (
        <FormSection section={sections.ReportContact}>
          <div className={styles.row}>
            {createInput(formFields.reportContactFirst, "45%")}
            {createInput(formFields.reportContactLast, "45%")}
          </div>

          <div className={styles.row}>
            {createInput(formFields.reportContactPhone, "45%", "tel")}
            {createInput(formFields.reportContactExt, "15%")}
            {createSelect(formFields.reportContactPhoneType, "30%", phoneType)}
          </div>
          <div className={styles.row}>
            {createInput(formFields.reportContactEmail, "45%", "email")}
          </div>
          {insuredUser && (
            <div className={styles.row}>
              {createSelect(
                formFields.reportContactDocumentDelivery,
                "94%",
                documentDelivery
              )}
            </div>
          )}
        </FormSection>
      )}

      <FormSection section={sections.IncidentDetails}>
        <div className={styles.row}>
          {createDateInput(formFields.incidentDate, "45%")}
          {createDateInput(formFields.employerNotifiedDate, "45%")}
        </div>
        <div className={styles.row}>
          {createTextArea(formFields.incidentDescription, "90%")}
        </div>
      </FormSection>
    </>,
    <>
      {/*Begin Page 2 */}
      <FormSection section={sections.LocationInfo}>
        {locations && locations.length > 0 && (
          <button
            onClick={(event) => {
              event.preventDefault();
              setEnterLocation(!enterLocation);
            }}
            className={styles.emphasizedLink}
          >
            {enterLocation ? "Return" : "Enter your location"}
          </button>
        )}
        {enterLocation ? (
          <>
            <div className={styles.row}>
              {createInput(formFields.clientLocationName, "70%")}
              {createInput(formFields.clientLocationNumber, "20%")}
            </div>
            <div className={styles.row}>
              {createInput(formFields.clientLocationAddr, "45%")}
              {createInput(formFields.clientLocationAddr2, "45%")}
            </div>
            <div className={styles.row}>
              {createInput(formFields.clientLocationCity, "45%")}
              {createSearchableSelect(
                  formFields.clientLocationState,
                  "45%",
                  statesData
                  .filter(state => state.country.name === formFields.clientLocationCountry.value)
                  .map(state => state.state)
                )}
            </div>
            <div className={styles.row}>
              {createInput(formFields.clientLocationZip, "45%")}
              {createInput(formFields.clientLocationCounty, "45%")}
            </div>
            <div className={styles.row}>
              {createSelect(
                  formFields.clientLocationCountry,
                  "35%",
                  [US.name]
                )}
              {createInput(formFields.clientLocationPhone, "35%", "tel")}
              {createInput(formFields.clientLocationExt, "20%")}
            </div>
          </>
        ) : (
          <div className={styles.row}>
            {locationDetails.length === 1
              ? createSelect(
                  formFields.theLocationInfo,
                  "70%",
                  locationDetails,
                  true,
                  false
                )
              : createSearchableSelect(
                  formFields.theLocationInfo,
                  "70%",
                  locationDetails,
                  false,
                  "Start typing to find your location"
                )}
          </div>
        )}
        <div className={styles.row}>
          {createToggleInput(formFields.atClientLocation, "50%", yesNo)}
        </div>
      </FormSection>

      {formFields.atClientLocation.value === false && (
        <FormSection section={sections.IncLocation}>
          <div className={styles.row}>
            {createInput(formFields.incidentLocationName, "45%")}
          </div>
          <div className={styles.row}>
            {createInput(formFields.incidentLocationAddr, "45%")}
            {createInput(formFields.incidentLocationAddr2, "45%")}
          </div>
          <div className={styles.row}>
            {createInput(formFields.incidentLocationCity, "45%")}
            {createSearchableSelect(
                formFields.incidentLocationState,
                "45%",
                statesData
                  .filter(state => state.country.name === formFields.incidentLocationCountry.value)
                  .map(state => state.state),
                false
              )}
          </div>
          <div className={styles.row}>
            {createInput(formFields.incidentLocationZip, "30%")}
            {createInput(formFields.incidentLocationCounty, "30%")}
            {countries &&
              createSelect(
                formFields.incidentLocationCountry,
                "30%",
                countries
              )}
          </div>
        </FormSection>
      )}
    </>,
    <>
      {/*Begin Page 3 */}
      <FormSection section={sections.Claimant}>
        <div className={styles.row}>
          {createInput(formFields.claimantFirst, "40%")}
          {createInput(formFields.claimantMidInitial, "10%")}
          {createInput(formFields.claimantLast, "40%")}
        </div>

        <div className={styles.row}>
          {createInput(formFields.claimantAddr, "46%")}
          {createInput(formFields.claimantAddr2, "46%")}
        </div>

        <div className={styles.row}>
          {createInput(formFields.claimantCity, "46%")}
          {createSearchableSelect(
            formFields.claimantState,
            "46%",
            statesData
                  .filter(state => state.country.name === formFields.claimantCountry.value)
                  .map(state => state.state)
            )}
        </div>

        <div className={styles.row}>
          {createInput(formFields.claimantZip, "30%")}
          {createInput(formFields.claimantCounty, "30%")}
          {countries &&
            createSelect(formFields.claimantCountry, "30%", countries)}
        </div>

        <div className={styles.row}>
          {createInput(formFields.claimantBestPhone, "46%", "tel")}
          {createSelect(formFields.claimantPhoneType, "46%", phoneType)}
        </div>
        <div className={styles.row}>
          {createInput(formFields.claimantEmail, "30%", "email")}
          {createSecretInput(formFields.claimantSSN, "30%")}
          {createSelect(formFields.claimantGender, "30%", gender)}
        </div>

        <div className={styles.row}>
          {createSelect(formFields.claimantMaritalStatus, "30%", maritalStatus)}
          {createInput(formFields.claimantNumberDependents, "30%", "number")}
          {createDateInput(formFields.claimantDateOfBirth, "30%", true)}
        </div>

        <div className={styles.row}>
          {createToggleInput(formFields.fatalityInvolved, "46%", yesNo)}
        </div>

        {formFields.fatalityInvolved.value && (
          <div className={styles.row}>
            {createDateInput(formFields.dateOfDeath, "46%", true)}
          </div>
        )}
      </FormSection>

      <FormSection section={sections.Supervisor}>
        <div className={styles.row}>
          {createInput(formFields.supervisorFirst, "40%")}
          {createInput(formFields.supervisorMidInitial, "10%")}
          {createInput(formFields.supervisorLast, "40%")}
        </div>

        <div className={styles.row}>
          {createInput(formFields.supervisorTitle, "40%")}
          {createInput(formFields.supervisorPhone, "35%", "tel")}
          {createInput(formFields.supervisorExt, "15%")}
        </div>

        <div className={styles.row}>
          {createInput(formFields.supervisorEmail, "40%", "email")}
        </div>
      </FormSection>

      <FormSection section={sections.LostTime}>
        <div className={styles.row}>
          {createSelect(formFields.lostTime, "30%", yesNoNA)}
          {createSelect(formFields.salaryContinued, "30%", yesNoNA)}
          {createSelect(formFields.receivedFullWage, "30%", yesNoNA)}
        </div>
        <div className={styles.row}>
          {createDateInput(formFields.lastDateWorked, "45%", true)}
        </div>
        <div className={styles.row}>
          {createToggleInput(formFields.returnedToWork, "50%", yesNo)}
        </div>
        {formFields.returnedToWork.value && (
          <div className={styles.row}>
            {createDateInput(formFields.returnedDate, "45%", true)}
            {createSelect(formFields.returnedDuty, "45%", duty)}
          </div>
        )}
      </FormSection>

      <FormSection section={sections.Employment}>
        <div className={styles.row}>
          {createInput(formFields.jobTitle, "46%")}
          {createSearchableSelect(
            formFields.jobStatus,
            "46%",
            employmentStatusGW
          )}
        
        </div>
        <div className={styles.row}>
          {createDateInput(formFields.jobHireDate, "30%", true)}
          {createDateInput(formFields.jobTerminationDate, "30%", true)}
          {createSearchableSelect(formFields.jobHireState, "30%", statesData
                  .filter(state => state.country.name === US.name)
                  .map(state => state.state))
          }
        </div>
        <div className={styles.row}>
          {createInput(formFields.jobWage, "46%", "number")}
          {createSelect(
            formFields.jobWageFrequency,
            "46%",
            wagePer,
            false,
            false,
            true
          )}
        </div>
          <div className={styles.row}>
            {createSelect(formFields.jobPayPeriod, "46%", payPeriod)}
            {createInput(formFields.jobAverageHoursPerDay, "46%", "number")}
          </div>
          <div className={styles.row}>
            <p className={styles.emphasizedPar}>
              Which days does the employee usually work?
            </p>
          </div>
          <div className={styles.row}>
            {createCheckbox(formFields.jobSun, "20%")}
            {createCheckbox(formFields.jobMon, "20%")}
            {createCheckbox(formFields.jobTues, "20%")}
            {createCheckbox(formFields.jobWed, "20%")}
          </div>
          <div className={styles.row}>
            {createCheckbox(formFields.jobThurs, "20%")}
            {createCheckbox(formFields.jobFri, "20%")}
            {createCheckbox(formFields.jobSat, "20%")}
          </div>
      </FormSection>

      <FormSection section={sections.InjuryInfo}>
        <div className={styles.row}>
          {createToggleInput(formFields.injuryIs, "50%", yesNo)}
        </div>
        {formFields.injuryIs.value !== null && (
          <>
            <div className={styles.row}>
              {formFields.injuryIs.value &&
                createToggleInput(
                  formFields.injuryTreatmentSought,
                  "50%",
                  yesNo
                )}
            </div>
            <div className={styles.row}>
              {injuryParts &&
                formFields.injuryIs.value &&
                createSearchableSelect(
                  formFields.injuryPart,
                  "95%",
                  injuryParts
                )}
            </div>
            <div className={styles.row}>
              {injuryCauses &&
                createSearchableSelect(
                  formFields.injuryCause,
                  "95%",
                  injuryCauses
                )}
            </div>
            <div className={styles.row}>
              {injuryNatures &&
                formFields.injuryIs.value &&
                createSearchableSelect(
                  formFields.injuryNature,
                  "95%",
                  injuryNatures
                )}
            </div>
          </>
        )}
      </FormSection>

      {formFields.injuryTreatmentSought.value && (
        <FormSection section={sections.MedicalTreatment}>
          <div className={styles.row}>
            {createInput(formFields.hospitalName, "46%")}
          </div>

          <div className={styles.row}>
            {createInput(formFields.hospitalAddr, "46%")}
            {createInput(formFields.hospitalAddr2, "46%")}
          </div>
          <div className={styles.row}>
            {createInput(formFields.hospitalCity, "46%")}
            {createSearchableSelect(
                formFields.hospitalState,
                "46%",
                statesData
                  .filter(state => state.country.name === formFields.hospitalCountry.value)
                  .map(state => state.state))}
          </div>
          <div className={styles.row}>
            {createInput(formFields.hospitalZip, "30%")}
            {createInput(formFields.hospitalCounty, "30%")}
            {countries &&
              createSelect(formFields.hospitalCountry, "30%", countries)}
          </div>
          <div className={styles.row}>
            {createInput(formFields.hospitalPhone, "46%", "tel")}
            {createInput(formFields.hospitalExt, "46%")}
          </div>
          <div className={styles.row}>
            {createSelect(
              formFields.hospitalTransportation,
              "30%",
              transportation
            )}
            {createInput(formFields.doctorFirst, "30%")}
            {createInput(formFields.doctorLast, "30%")}
          </div>
          <div className={styles.row}>
            {createToggleInput(formFields.hospitalAddrDiffers, "50%", yesNo)}
          </div>
          {formFields.hospitalAddrDiffers.value && (
            <>
              <div className={styles.row}>
                {createInput(formFields.doctorAddr, "46%")}
                {createInput(formFields.doctorAddr2, "46%")}
              </div>
              <div className={styles.row}>
                {createInput(formFields.doctorCity, "46%")}
                {createSearchableSelect(
                    formFields.doctorState,
                    "46%",
                    statesData
                      .filter(state => state.country.name === formFields.doctorCountry.value)
                      .map(state => state.state))}
              </div>
              <div className={styles.row}>
                {createInput(formFields.doctorZip, "30%")}
                {createInput(formFields.doctorCounty, "30%")}
                {countries &&
                  createSelect(formFields.doctorCountry, "30%", countries)}
              </div>
            </>
          )}
        </FormSection>
      )}
    </>,
    <>
      {/*Begin Page 4 */}
      <FormSection section={sections.WitnessInfo}>
        <ol>
          {witnessInfo.map((witness, index) => createWitness(witness, index))}
        </ol>
        {witnessInfo.length < maxWitnesses && (
          <button
            onClick={(event) => {
              event.preventDefault();
              const newWitnesses = [...witnessInfo];
              const newWitness = { ...witnessObj };
              for (let fieldName in newWitness) {
                newWitness[fieldName] = {
                  ...newWitness[fieldName],
                  fieldName: `${newWitness[fieldName].fieldName}${witnessInfo.length}`,
                };
              } //destructuring inner objects so it shouldn't point to witnessObj fields and changing fieldNames so should be unique
              newWitnesses.push(newWitness);
              setWitnessInfo(newWitnesses);
            }}
            className={styles.emphasizedLink}
          >
            Add Witness
          </button>
        )}
      </FormSection>
    </>,
    <>
      {/*Begin Page 5 */}
      <FormSection section={sections.Notes}>
        <div className={styles.row}>
          {createTextArea(formFields.comments, "90%")}
        </div>
      </FormSection>

      <FormSection section={sections.Escalation}>
        <div className={styles.row}>
          {createToggleInput(formFields.escalationCriteria, "50%", yesNo)}
        </div>
        <ul className={styles.esList}>
          {escalationCriteria?.map((item) => (
            <li key={item} className={styles.boldListItem}>
              {item}
            </li>
          ))}
        </ul>
      </FormSection>

      <FormSection section={sections.Documents}>
        <UploadFile
          getFiles={(files) => handleDocuments(files, "add")}
          size={(documents.size / config.totalFileSizeLimitDivisor).toFixed(2)}
        />

        <div className="documentContainer">
          {documents.files &&
            documents.files.map((document, index) => {
              return (
                <FileIcon
                  key={index}
                  file={document}
                  removeFile={(file) => handleDocuments(file, "remove")}
                />
              );
            })}
        </div>
      </FormSection>
    </>,
    previewData?.length > 0 && <PreviewSubmission formData={previewData} />,
  ]; //contains all of the form content, split into pages

  let lastPage = pages.length - 1;
  function next() {
    if (validatePage()) {
      if (page === lastPage - 1) {
        createPreviewData();
      }
      setPage(Math.min(page + 1, lastPage));
      setPageInvalid(false);
      setScrollTarget(styles.message);
    } else {
      setPageInvalid(true);
    }
  } //to move to the next page
  function previous() {
    setPage(Math.max(0, page - 1));
    setScrollTarget(styles.message);
    setPageInvalid(false);
  } //to move to the previous page

  const buttons = (
    <div className={styles.buttonMessHolder}>
      <div
        className={`${styles.buttonHolder} ${
          page === lastPage && styles.onlyPrevious
        } ${page === 0 && styles.onlyNext}`}
      >
        {page !== 0 && (
          <>
            <button
              type="button"
              className={styles.arrowButton}
              onClick={previous}
              aria-label="Next Page"
            >
              <div className={styles.arrowCircle}>
                <FontAwesomeIcon
                  className={`${styles.arrow} ${styles.backArrow}`}
                  icon={faChevronLeft}
                  size="2x"
                />
              </div>
              <p className={styles.svgText}>BACK</p>
            </button>
          </>
        )}
        {page !== lastPage && (
          <button
            type="button"
            className={styles.arrowButton}
            onClick={next}
            aria-label="Next Page"
          >
            <span className={styles.svgText}>NEXT</span>
            <div className={styles.arrowCircle}>
              <FontAwesomeIcon
                className={`${styles.bottomArrow} ${styles.nextArrow}`}
                icon={faChevronRight}
                size="2x"
              />
            </div>
          </button>
        )}
      </div>
      {pageInvalid && <p className="redMessage">{validationError}</p>}
    </div>
  ); //page navigation buttons element

  const mapDataOptions = (array, localArray, setState) => {
    if (localArray?.length < 1) {
      const newArray = array.map((item) => concatenateOption(item));
      setState(newArray);
    }
  }; //maps data into options

  useEffect(() => {
    dispatch(appActions.setLoading(true));
    dispatch(formActions.getWCParts());
    dispatch(formActions.getWCCauses());
    dispatch(formActions.getWCNatures());
  }, [dispatch]); //retrieve constant data from endpoints

  useEffect(() => {
    dispatch(formActions.clearPolicyAssets());
    setFormFields(fields);
    setValidatorsAdded(false);
    setEnterLocation(true);
    setPageInvalid(false);
    setShouldValidate(false);
    setPreviewData([]);
    setWitnessInfo([]);
    setNoWitnessData([]);
    setDocuments({ files: [], size: 0 });
    setPage(0);
  }, [policyNumber]); //eslint-disable-line react-hooks/exhaustive-deps
  //get location data for policy and reset form state when policy number changes

    useEffect(() => {
      if (policyNumber && formFields.incidentDate.value) {
        if (
            policyData?.policyNumber !== policyNumber ||
            !dateInRangeIncludesStart(
              formFields.incidentDate.value,
              policyData.effectiveDate,
              policyData.expirationDate
              )
            ) {
                //only retrieve again if actual change in policy or term
                dispatch(formActions.clearPolicyAssets());
                dispatch(
                    formActions.policySearchRetrieve(
                        formFields.incidentDate.value.format("YYYY-MM-DD"),
                        policyNumber
                    )
                );
            }
        } else {
            dispatch(formActions.clearPolicyAssets());
        }//if policy or incident date set to blank, clear policy
    }, [dispatch, formFields.incidentDate.value, policyNumber]);

  useEffect(() => {
    if (countriesData?.length > 0 && countries?.length < 1) {
      setCountries(countriesData.map((item) => item.name));
    }
    injuryPartsData?.length > 0 &&
      mapDataOptions(injuryPartsData, injuryParts, setInjuryParts);
    injuryCausesData?.length > 0 &&
      mapDataOptions(injuryCausesData, injuryCauses, setInjuryCauses);
    injuryNaturesData?.length > 0 &&
      mapDataOptions(injuryNaturesData, injuryNatures, setInjuryNatures);
  }, [injuryPartsData, injuryCausesData, injuryNaturesData]); //eslint-disable-line react-hooks/exhaustive-deps
  //maps constant data into options

  useEffect(() => {
    if (
      injuryPartsData?.length > 0 &&
      injuryCausesData?.length > 0 &&
      injuryNaturesData?.length > 0
    ) {
      dispatch(appActions.setLoading(false));
    }
    //   eslint-disable-next-line react-hooks/exhaustive-deps
  }, [injuryPartsData, injuryCausesData, injuryNaturesData, dispatch]); //stops spinner when significant constants loaded

  useEffect(() => {
    if (formFields && !validatorsAdded) {
      addValidatorsNames(formFields, setFormFields);
      addValidatorsNames(witness, setWitnessObj);
      setValidatorsAdded(true);
    }
  }, [formFields, validatorsAdded]); //eslint-disable-line react-hooks/exhaustive-deps
  //adds validators to fields

  const uniqueOptions = (propName, array) => [
    ...new Set(array.map((item) => item[propName])),
  ]; //helper for creating prefilled location dropdowns - so cities and states don't duplicate
    const createLocationDetail = ((location) => {
      return  `${location.locationNumber} - ${location.addressLine1}, ${location.city}, ${stateCodeToName(location.state)}, ${location.postalCode}`; //helper for populating "Location" dropdown
    });

    useEffect(() => {
      if (locations && locations.length > 0) {
        setLocationDetails(
          uniqueOptions(
            locationInfo,
            locations.map((location) => {
              return { [locationInfo]: createLocationDetail(location) };
            })
          )
        );
        setEnterLocation(false);
      } else {
        setEnterLocation(true);
      }
    }, [locations]); //eslint-disable-line react-hooks/exhaustive-deps
  //set field value options based on location data retrieved

  useEffect(() => {
    if (!enterLocation && validatorsAdded && locations?.length > 0) {
      const newFields = { ...formFields };
      if (locations.length === 1) {
        const theLocation = createLocationDetail(locations[0]);
        newFields.theLocationInfo.value = theLocation;
        newFields.theLocationInfo.displayValue = {
          label: theLocation,
          value: theLocation,
        };
      }
      setFormFields(newFields);
    }
  }, [enterLocation, validatorsAdded, locations]); //eslint-disable-line react-hooks/exhaustive-deps
  //prefill location to first location retrieved every time enterLocation becomes false

    useEffect(() => {
    if (validatorsAdded) {
      const newFields = { ...formFields };
      if (locations?.length > 0 && formFields.theLocationInfo.value && !enterLocation) {
        const locationNumber = formFields.theLocationInfo.value.split("-")[0].trim();
        const location = locations.find(
                    (location) => location.locationNumber === locationNumber
                );
        const state = location.state;
        handleFieldChange(state, newFields.incidentLocationState, true, { label: state, value: state });
      } else if (formFields.clientLocationState.value) {
        handleFieldChange(formFields.clientLocationState.value, newFields.incidentLocationState, true, formFields.clientLocationState.displayValue);
      }
      setFormFields(newFields);
    }
  }, [formFields.clientLocationState.value, formFields.theLocationInfo.value])
  //default incident location state to associated policy location state

  //below useEffects clear the state value if the country changed - since the options have changed
  useEffect(() => {
    if (validatorsAdded) {
      const newFields = { ...formFields };
      handleFieldChange("", newFields.incidentLocationState, true, null);
      setFormFields(newFields);
    }
  }, [formFields.incidentLocationCountry.value])

   useEffect(() => {
    if (validatorsAdded) {
      const newFields = { ...formFields };
      handleFieldChange("", newFields.claimantState, true, null);
      setFormFields(newFields);
    }
  }, [formFields.claimantCountry.value])

   useEffect(() => {
    if (validatorsAdded) {
      const newFields = { ...formFields };
      handleFieldChange("", newFields.hospitalState, true, null);
      setFormFields(newFields);
    }
  }, [formFields.hospitalCountry.value])

   useEffect(() => {
    if (validatorsAdded) {
      const newFields = { ...formFields };
      handleFieldChange("", newFields.doctorState, true, null);
      setFormFields(newFields);
    }
  }, [formFields.doctorCountry.value])

  useEffect(() => {
    if (formFields.injuryIs.value === false) {
      const newFields = { ...formFields };
      newFields.injuryCause.label = "What caused the incident?";
      handleFieldChange(noPhysicalInjury, formFields.injuryNature, true);
      handleFieldChange("", formFields.injuryPart);
      newFields.injuryTreatmentSought.value = false;
      newFields.hospitalAddrDiffers.value = false;
      setFormFields(newFields);
    } else if (formFields.injuryIs.value === true) {
      const newFields = { ...formFields };
      newFields.injuryCause.label = fields.injuryCause.label;
      newFields.injuryTreatmentSought.value = true;
      setFormFields(newFields);
    }
  }, [formFields.injuryIs.value, injuryParts, injuryCauses, injuryNatures]); //eslint-disable-line react-hooks/exhaustive-deps
  //to set all injury information to none if no injury

  const updateValidation = (
    field,
    minDateField,
    maxDateField,
    time = false
  ) => {
    if (minDateField) {
      field.additionalProps = {
        ...field.additionalProps,
        minDate: time
          ? dateToMoment(new Date(momentToDate(minDateField.value)))
          : dateToMoment(new Date(momentToDate(minDateField.value)).setHours(0, 0, 0)),
      };
    }
    if (maxDateField) {
      field.additionalProps = {
        ...field.additionalProps,
        maxDate: time
          ? dateToMoment(new Date(momentToDate(minDateField.value)))
          : dateToMoment(new Date(momentToDate(maxDateField.value)).setHours(23, 59, 59)),
      };
    }
    field.validation = createValidator({
      ...field.additionalProps,
      ...field.regex,
      label: field.label,
      required: field.required,
      minDateField: minDateField?.label,
      maxDateField: maxDateField?.label,
    });
  }; //updates date field props and validation function for dependent fields
  useEffect(() => {
    if (formFields.incidentDate.value) {
      const newFields = { ...formFields };
      updateValidation(
        newFields.employerNotifiedDate,
        formFields.incidentDate,
        null,
        true
      );
      updateValidation(newFields.jobHireDate, null, formFields.incidentDate);
      updateValidation(newFields.returnedDate, formFields.incidentDate);
      setFormFields(newFields);
    }
  }, [formFields.incidentDate.value]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (formFields.jobHireDate.value) {
      const newFields = { ...formFields };
      updateValidation(newFields.jobTerminationDate, formFields.jobHireDate);
      updateValidation(newFields.lastDateWorked, formFields.jobHireDate);
      setFormFields(newFields);
    }
  }, [formFields.jobHireDate.value]); //eslint-disable-line react-hooks/exhaustive-deps

  const reValidate = (field) =>
    field.validation &&
    field.value &&
    handleFieldChange(field.value, field, true); //helper for revalidating components after their validation function has changed (for dependent fields)

  useEffect(() => {
    reValidate(formFields.employerNotifiedDate);
  }, [formFields.employerNotifiedDate.validation]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    reValidate(formFields.jobHireDate);
  }, [formFields.jobHireDate.validation]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    reValidate(formFields.returnedDate);
  }, [formFields.returnedDate.validation]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    reValidate(formFields.jobTerminationDate);
  }, [formFields.jobTerminationDate.validation]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    reValidate(formFields.lastDateWorked);
  }, [formFields.lastDateWorked.validation]); //eslint-disable-line react-hooks/exhaustive-deps
  //above useEffects revalidate dependent fields when their validation function changes

  useEffect(() => {
    if (
      validatorsAdded &&
      yesNoNA &&
      formFields?.lostTime?.value === yesNoNA[0]
    ) {
      const newFields = { ...formFields };
      newFields.lastDateWorked.required = true;
      newFields.lastDateWorked.validation = createValidator({
        ...newFields.lastDateWorked.additionalProps,
        ...newFields.lastDateWorked.regex,
        label: newFields.lastDateWorked.label,
        required: newFields.lastDateWorked.required,
      });
      setFormFields(newFields);
    } else if (validatorsAdded) {
      const newFields = { ...formFields };
      newFields.lastDateWorked.required = false;
      newFields.lastDateWorked.validation = createValidator({
        ...newFields.lastDateWorked.additionalProps,
        ...newFields.lastDateWorked.regex,
        label: newFields.lastDateWorked.label,
        required: newFields.lastDateWorked.required,
      });
      setFormFields(newFields);
    }
  }, [formFields?.lostTime?.value, validatorsAdded]);

  useEffect(() => {
    if (scrollTarget) {
      scrollAnchor.current.click();
    } else {
      appActions.scrollToTop("#scrollable");
    }
    if (page === 1 && !locations) {
      dispatch(appActions.setLoading(true));
    }
  }, [page, scrollTarget]); //scroll to top or to saved anchor on page change

  useEffect(() => {
    if (pageInvalid) {
      if (shouldValidate) {
        if (validatePage()) {
          setPageInvalid(false);
        }
        setShouldValidate(false);
      } else {
        setShouldValidate(true);
      }
    }
  }, [formFields, witnessInfo]); //eslint-disable-line react-hooks/exhaustive-deps
  //to display or hide validation message for page

  useEffect(() => {
    const height = formContainer?.current?.clientHeight;
    if (height > window.innerHeight - 250) {
      setShow2Buttons(true);
    } else {
      setShow2Buttons(false);
    }
  }, [page, witnessInfo.length]); //to show or hide bottom of page buttons if top ones not visible when scroll

  return (
    <>
      {apiError ? (
        <div className={styles.extraSpace}>
          <NoData message={apiFailureError} />
        </div>
      ) : (
        <section className={styles.container} ref={formContainer}>
          <span className="redMessage" id={styles.message}>
            {mandatoryFieldsMessage}
          </span>
          {buttons}
          {
            //eslint-disable-next-line
            <a
              href={`#${scrollTarget}`}
              className={styles.hiddenAnchor}
              ref={scrollAnchor}
            ></a>
          }
          <Wizard page={page}>{pages}</Wizard>

          {show2Buttons && buttons}
          {page === lastPage && (
            <div className={styles.submitWrapper}>
              <button
                type="submit"
                onClick={(event) => {
                  event.preventDefault();
                  submitForm();
                }}
                className={styles.submitButton}
              >
                Submit Claim
              </button>
            </div>
          )}
          <div className={styles.extraSpace}>
            <StatusBar
              bgcolor={colors.primary}
              completed={page + 1}
              total={pages.length}
              message="Claim Progress"
            />
          </div>
        </section>
      )}
    </>
  );
}

export default WCMainForm;
